import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Plus,
  Trash2,
  Utensils,
} from 'lucide-react';

import MenuItemQuickCreateModal from './MenuItemQuickCreateModal';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import CompactInputField from '../../../components/Forms/Input/CompactInputField';
import CompactSelectField from '../../../components/Forms/Input/CompactSelectField';
import TextAreaField from '../../../components/Forms/Input/TextAreaField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';

import { IProductUnit } from '../../../types/productUnit/productUnitInterfaces';
import { RestaurantMenuItemDTO } from '../../../types/restaurant/RestaurantMenuTypes';
import { RestaurantRawMaterialDTO } from '../../../types/restaurant/RestaurantRawMaterialTypes';
import {
  RestaurantRecipeForm,
  RestaurantRecipeIngredientForm,
} from '../../../types/restaurant/RestaurantRecipeTypes';
import { isRestaurantRecipeInventoryMode } from '../../../services/restaurant/RestaurantRecipeService';
import { RestaurantUnitConversionDTO } from '../../../types/restaurant/RestaurantUnitConversionTypes';

import RestaurantRecipeProductionPanel from '../production/RestaurantRecipeProductionPanel';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import RestaurantRawMaterialService from '../../../services/restaurant/RestaurantRawMaterialService';
import PagedRawMaterialPicker from './components/PagedRawMaterialPicker';

interface Props {
  isOpen: boolean;
  branchId: number | '';
  form: RestaurantRecipeForm;
  menuItems: RestaurantMenuItemDTO[];
  rawMaterials: RestaurantRawMaterialDTO[];
  units: IProductUnit[];
  conversions: RestaurantUnitConversionDTO[];
  saving: boolean;
  loadingOptions: boolean;
  onClose: () => void;
  onChange: <K extends keyof Omit<RestaurantRecipeForm, 'ingredients'>>(
    field: K,
    value: RestaurantRecipeForm[K]
  ) => void;
  onIngredientChange: <
    K extends keyof Omit<RestaurantRecipeIngredientForm, 'key'>
  >(
    key: number,
    field: K,
    value: RestaurantRecipeIngredientForm[K]
  ) => void;
  onAddIngredient: () => void;
  onRemoveIngredient: (key: number) => void;
  onMoveIngredient: (key: number, direction: -1 | 1) => void;
  onCreateMenuItem: (input: {
    productId: number;
    categoryId: number;
    displayName: string;
  }) => Promise<RestaurantMenuItemDTO | null>;
  onSave: () => void;
}

const RestaurantRecipeFormModal = ({
  isOpen,
  branchId,
  form,
  menuItems,
  rawMaterials,
  units,
  conversions,
  saving,
  loadingOptions,
  onClose,
  onChange,
  onIngredientChange,
  onAddIngredient,
  onRemoveIngredient,
  onMoveIngredient,
  onCreateMenuItem,
  onSave,
}: Props) => {
  const [materialCache, setMaterialCache] = useState<Record<number, RestaurantRawMaterialDTO>>({});
  const [pickingIngredientKey, setPickingIngredientKey] = useState<number | null>(null);
  const [openNotes, setOpenNotes] = useState<Set<number>>(new Set());

  const toggleNote = (key: number) =>
    setOpenNotes((current) => {
      const next = new Set(current);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Al abrir/cargar la receta, muestra abiertas las notas que ya traen texto.
  useEffect(() => {
    if (!isOpen) return;
    setOpenNotes((current) => {
      const next = new Set(current);
      form.ingredients.forEach((ingredient) => {
        if (ingredient.notes) next.add(ingredient.key);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const [menuItemQuickOpen, setMenuItemQuickOpen] = useState(false);
  const [menuItemQuickSaving, setMenuItemQuickSaving] = useState(false);

  // Si el nombre de la receta sigue vacío, tómalo del ítem de menú elegido.
  const prefillRecipeName = (label?: string) => {
    if (label && !form.name.trim()) {
      onChange('name', label);
    }
  };

  const handleCreateMenuItem = async (input: {
    productId: number;
    categoryId: number;
    displayName: string;
  }) => {
    setMenuItemQuickSaving(true);
    try {
      const created = await onCreateMenuItem(input);
      if (created) {
        onChange('menuItemId', created.id);
        prefillRecipeName(created.displayName || created.productName);
        setMenuItemQuickOpen(false);
      }
    } finally {
      setMenuItemQuickSaving(false);
    }
  };

  useEffect(() => {
    setMaterialCache((current) => {
      const next = { ...current };
      rawMaterials.forEach((material) => { next[material.id] = material; });
      return next;
    });
  }, [rawMaterials]);

  useEffect(() => {
    if (!isOpen) return;
    const missingIds = form.ingredients
      .map((ingredient) => Number(ingredient.rawMaterialId))
      .filter((id) => id > 0 && !materialCache[id]);
    if (!missingIds.length) return;
    Promise.all([...new Set(missingIds)].map((id) => RestaurantRawMaterialService.get(id)))
      .then((responses) => setMaterialCache((current) => {
        const next = { ...current };
        responses.forEach((response) => { next[response.data.id] = response.data; });
        return next;
      }));
  }, [form.ingredients, isOpen, materialCache]);
  // =====================================================
  // 🔹 Validaciones
  // =====================================================

  const errors = useMemo(() => {
    const result: Record<string, string> = {};

    if (!form.menuItemId) {
      result.menuItemId = 'Seleccione un ítem de menú.';
    }

    if (!form.name.trim()) {
      result.name = 'El nombre es obligatorio.';
    } else if (form.name.trim().length > 150) {
      result.name = 'Máximo 150 caracteres.';
    }

    if (form.description && form.description.length > 500) {
      result.description = 'Máximo 500 caracteres.';
    }

    if (!form.yieldUnitId) {
      result.yieldUnitId = 'Seleccione la unidad de rendimiento.';
    }

    if (form.yieldQuantity === '' || Number(form.yieldQuantity) <= 0) {
      result.yieldQuantity = 'El rendimiento debe ser mayor que cero.';
    }

    if (!isRestaurantRecipeInventoryMode(form.inventoryMode)) {
      result.inventoryMode = 'Seleccione un modo de consumo válido.';
    }

    if (!form.ingredients.length) {
      result.ingredients = 'Agregue al menos un ingrediente.';
    }

    const selectedMaterials = new Set<number>();

    form.ingredients.forEach((ingredient) => {
      const prefix = `ingredient-${ingredient.key}`;

      const rawMaterialId = Number(ingredient.rawMaterialId);

      if (!ingredient.rawMaterialId) {
        result[`${prefix}-rawMaterialId`] = 'Seleccione una materia prima.';
      } else if (selectedMaterials.has(rawMaterialId)) {
        result[`${prefix}-rawMaterialId`] = 'La materia prima está duplicada.';
      } else {
        selectedMaterials.add(rawMaterialId);
      }

      if (ingredient.quantity === '' || Number(ingredient.quantity) <= 0) {
        result[`${prefix}-quantity`] = 'La cantidad debe ser mayor que cero.';
      }

      if (!ingredient.unitId) {
        result[`${prefix}-unitId`] = 'Seleccione una unidad.';
      }

      if (ingredient.notes && ingredient.notes.length > 500) {
        result[`${prefix}-notes`] = 'Máximo 500 caracteres.';
      }
    });

    return result;
  }, [form]);

  // =====================================================
  // 🔹 Estado del formulario
  // =====================================================

  const hasErrors = Object.keys(errors).length > 0;

  const activeUnits = useMemo(
    () => units.filter((unit) => unit.status !== false),
    [units]
  );

  // =====================================================
  // 🔹 Opciones de materias primas
  // =====================================================

  // =====================================================
  // 🔹 Unidades compatibles
  // =====================================================

  const getCompatibleUnits = (rawMaterialId: number | '') => {
    const material = materialCache[Number(rawMaterialId)];

    if (!material) {
      return [];
    }

    const conversionGraph = new Map<number, Set<number>>();

    conversions
      .filter(
        (conversion) =>
          conversion.active &&
          (conversion.global || conversion.rawMaterialId === material.id)
      )
      .forEach((conversion) => {
        if (!conversionGraph.has(conversion.fromUnitId)) {
          conversionGraph.set(conversion.fromUnitId, new Set());
        }

        if (!conversionGraph.has(conversion.toUnitId)) {
          conversionGraph.set(conversion.toUnitId, new Set());
        }

        conversionGraph.get(conversion.fromUnitId)!.add(conversion.toUnitId);

        conversionGraph.get(conversion.toUnitId)!.add(conversion.fromUnitId);
      });

    const connectedUnits = new Set<number>([material.unitId]);

    const pendingUnits = [material.unitId];

    while (pendingUnits.length > 0) {
      const currentUnitId = pendingUnits.shift();

      if (currentUnitId === undefined) {
        continue;
      }

      conversionGraph.get(currentUnitId)?.forEach((nextUnitId) => {
        if (!connectedUnits.has(nextUnitId)) {
          connectedUnits.add(nextUnitId);
          pendingUnits.push(nextUnitId);
        }
      });
    }

    return activeUnits.filter((unit) => connectedUnits.has(unit.id));
  };

  // =====================================================
  // 🔹 Eventos
  // =====================================================

  const handleMaterialChange = (
    ingredient: RestaurantRecipeIngredientForm,
    material: RestaurantRawMaterialDTO
  ) => {
    setMaterialCache((current) => ({ ...current, [material.id]: material }));
    onIngredientChange(ingredient.key, 'rawMaterialId', material.id);
    onIngredientChange(ingredient.key, 'unitId', material.unitId);
    setPickingIngredientKey(null);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (saving || loadingOptions || hasErrors) {
      return;
    }

    onSave();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={form.id ? 'Editar receta' : 'Nueva receta'}
      maxWidth="max-w-4xl"
      headerExtra={<RestaurantFormHelp topic="recipe" />}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* =====================================================
            🔹 Datos generales
        ===================================================== */}

        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <div>
            <SimpleSearchSelect
              label="Ítem de menú"
              name="menuItemId"
              placeholder="Seleccione un ítem de menú"
              searchPlaceholder="Buscar por código o nombre..."
              options={menuItems.map((item) => ({
                id: item.id,
                code: item.code,
                name: item.code
                  ? `${item.code} · ${item.displayName || item.productName}`
                  : item.displayName || item.productName || `Ítem #${item.id}`,
              }))}
              selectedValue={
                form.menuItemId === '' ? null : Number(form.menuItemId)
              }
              iconKey="product"
              required
              disabled={loadingOptions || saving}
              error={errors.menuItemId}
              onSelect={(selectedItem) => {
                onChange('menuItemId', selectedItem.id);
                const picked = menuItems.find(
                  (item) => item.id === selectedItem.id
                );
                prefillRecipeName(
                  picked?.displayName || picked?.productName
                );
              }}
            />
            {branchId && (
              <button
                type="button"
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                disabled={saving}
                onClick={() => setMenuItemQuickOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                Crear ítem de menú
              </button>
            )}
          </div>

          <InputField
            label="Nombre de la receta"
            name="recipeName"
            type="text"
            iconKey="product"
            required
            disabled={saving}
            value={form.name}
            error={errors.name}
            onChange={(event) => onChange('name', event.target.value)}
          />

          <InputField
            label="Rendimiento"
            name="yieldQuantity"
            type="number"
            iconKey="stock"
            required
            min={0}
            step="0.000001"
            disabled={saving}
            value={form.yieldQuantity}
            error={errors.yieldQuantity}
            onChange={(event) =>
              onChange(
                'yieldQuantity',
                event.target.value === '' ? '' : Number(event.target.value)
              )
            }
          />

          <SimpleSearchSelect
            label="Unidad de rendimiento"
            name="yieldUnitId"
            placeholder="Seleccione una unidad"
            searchPlaceholder="Buscar unidad..."
            selectedValue={form.yieldUnitId ? Number(form.yieldUnitId) : null}
            iconKey="unitMeasure"
            required
            disabled={loadingOptions || saving}
            error={errors.yieldUnitId}
            options={activeUnits.map((unit) => ({
              id: unit.id,
              name: unit.unitCode
                ? `${unit.name} (${unit.unitCode})`
                : unit.name,
            }))}
            onSelect={(selected) => onChange('yieldUnitId', selected.id)}
          />

          <div className="md:col-span-2">
            <SelectField
              label="Modo de consumo"
              name="inventoryMode"
              value={form.inventoryMode}
              iconKey="stock"
              required
              disabled={saving}
              error={errors.inventoryMode}
              options={[
                {
                  value: 'DIRECT_ORDER_CONSUMPTION',
                  label: 'Consumir ingredientes al preparar/vender',
                },
                {
                  value: 'PRODUCTION_BATCH',
                  label: 'Consumir ingredientes al producir lote',
                },
              ]}
              onChange={(event) =>
                onChange(
                  'inventoryMode',
                  event.target.value as RestaurantRecipeForm['inventoryMode']
                )
              }
            />
            <p className="mt-1 text-xs text-bodydark2">
              {form.inventoryMode === 'PRODUCTION_BATCH'
                ? 'Descuenta materia prima al producir; la venta descuenta solo el producto terminado.'
                : 'Descuenta la materia prima directamente desde la comanda.'}
            </p>
          </div>
        </div>

        <TextAreaField
          label="Descripción (opcional)"
          name="recipeDescription"
          value={form.description}
          rows={2}
          maxLength={500}
          disabled={saving}
          placeholder="Notas de preparación..."
          error={errors.description}
          rightContent={`${form.description.length}/500`}
          onChange={(event) => onChange('description', event.target.value)}
        />

        {/* =====================================================
            🔹 Ingredientes
        ===================================================== */}

        <section className="overflow-visible rounded-xl border border-stroke dark:border-strokedark">
          <header className="flex items-center justify-between gap-3 border-b border-stroke bg-gray-2/50 px-3 py-2.5 dark:border-strokedark dark:bg-meta-4/50">
            <div className="flex items-center gap-2">
              <Utensils className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-black dark:text-white">
                Ingredientes
              </h3>
              <span className="text-xs text-bodydark2">
                ({form.ingredients.length})
              </span>
            </div>

            <ActionButton
              label="Agregar"
              icon={<Plus className="h-4 w-4" />}
              variant="outline"
              size="sm"
              fullWidth={false}
              disabled={saving}
              onClick={onAddIngredient}
              className="whitespace-nowrap"
            />
          </header>

          {form.ingredients.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-warning">
              {errors.ingredients}
            </p>
          )}

          {form.ingredients.length > 0 && (
            <div className="hidden gap-2 px-3 pb-1 pt-2.5 text-xs font-medium text-bodydark2 sm:flex">
              <span className="w-4 shrink-0" />
              <span className="flex-1">Materia prima *</span>
              <span className="w-24 shrink-0">Cantidad *</span>
              <span className="w-40 shrink-0">Unidad *</span>
              <span className="w-[92px] shrink-0" />
            </div>
          )}

          <div>
            {form.ingredients.map((ingredient, index) => {
              const prefix = `ingredient-${ingredient.key}`;
              const compatibleUnits = getCompatibleUnits(
                ingredient.rawMaterialId
              );
              const material = ingredient.rawMaterialId
                ? materialCache[Number(ingredient.rawMaterialId)]
                : undefined;
              const isFirst = index === 0;
              const isLast = index === form.ingredients.length - 1;
              const materialError = errors[`${prefix}-rawMaterialId`];
              const noteShown = openNotes.has(ingredient.key);

              return (
                <div
                  key={ingredient.key}
                  className={`border-b border-stroke py-2 last:border-b-0 dark:border-strokedark ${
                    index % 2 === 1
                      ? 'bg-meta-2 dark:bg-meta-4/50'
                      : 'bg-white dark:bg-boxdark'
                  }`}
                >
                  {/* Medio: una sola línea */}
                  <div className="flex flex-wrap items-start gap-2 px-3 sm:flex-nowrap">
                    <span className="hidden w-4 shrink-0 pt-2.5 text-center text-xs font-semibold text-bodydark2 sm:block">
                      {index + 1}
                    </span>

                    {/* Materia prima */}
                    <div className="min-w-[160px] flex-1">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => setPickingIngredientKey(ingredient.key)}
                        className={`bg-gray-1 flex h-10 w-full items-center justify-between gap-2 rounded-lg border px-3 text-left text-sm transition-colors hover:border-primary/60 disabled:opacity-70 dark:bg-meta-4 ${
                          materialError
                            ? 'border-danger'
                            : 'border-stroke dark:border-strokedark'
                        }`}
                      >
                        <span
                          className={
                            material
                              ? 'truncate text-black dark:text-white'
                              : 'text-body dark:text-bodydark2'
                          }
                        >
                          {material
                            ? `${material.code} · ${material.name}`
                            : 'Seleccionar materia prima'}
                        </span>
                        <span className="shrink-0 text-xs font-medium text-primary">
                          {material ? 'Cambiar' : 'Elegir'}
                        </span>
                      </button>
                      {materialError && (
                        <p className="mt-1 text-xs text-danger">
                          {materialError}
                        </p>
                      )}
                    </div>

                    {/* Cantidad */}
                    <CompactInputField
                      name={`${prefix}-quantity`}
                      type="number"
                      min={0}
                      step="0.000001"
                      placeholder="Cant."
                      disabled={saving}
                      value={ingredient.quantity}
                      error={errors[`${prefix}-quantity`]}
                      className="w-full shrink-0 sm:w-24"
                      onChange={(event) =>
                        onIngredientChange(
                          ingredient.key,
                          'quantity',
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value)
                        )
                      }
                    />

                    {/* Unidad de consumo */}
                    <CompactSelectField
                      name={`${prefix}-unit`}
                      value={ingredient.unitId}
                      placeholder="Unidad"
                      disabled={saving || !ingredient.rawMaterialId}
                      error={errors[`${prefix}-unitId`]}
                      className="w-full shrink-0 sm:w-40"
                      options={compatibleUnits.map((unit) => ({
                        value: unit.id,
                        label: unit.unitCode
                          ? `${unit.name} (${unit.unitCode})`
                          : unit.name,
                      }))}
                      onChange={(event) =>
                        onIngredientChange(
                          ingredient.key,
                          'unitId',
                          Number(event.target.value)
                        )
                      }
                    />

                    {/* Acciones */}
                    <div className="flex shrink-0 items-center pt-1">
                      <button
                        type="button"
                        title="Subir"
                        disabled={isFirst || saving}
                        onClick={() => onMoveIngredient(ingredient.key, -1)}
                        className="inline-flex h-8 w-7 items-center justify-center rounded text-bodydark2 hover:text-primary disabled:opacity-25"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Bajar"
                        disabled={isLast || saving}
                        onClick={() => onMoveIngredient(ingredient.key, 1)}
                        className="inline-flex h-8 w-7 items-center justify-center rounded text-bodydark2 hover:text-primary disabled:opacity-25"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Eliminar"
                        disabled={saving}
                        onClick={() => onRemoveIngredient(ingredient.key)}
                        className="inline-flex h-8 w-7 items-center justify-center rounded text-danger hover:bg-danger hover:text-white disabled:opacity-40"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Footer: nota desplegable */}
                  <div className="mt-2 border-t border-stroke bg-black/[0.03] dark:border-strokedark dark:bg-white/[0.04]">
                    <button
                      type="button"
                      onClick={() => toggleNote(ingredient.key)}
                      className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium text-bodydark2 transition-colors hover:text-primary"
                    >
                      <span>
                        Nota
                        {ingredient.notes ? '' : ' (opcional)'}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          noteShown ? 'rotate-180 text-primary' : ''
                        }`}
                      />
                    </button>
                    {noteShown && (
                      <div className="px-3 pb-2.5">
                        <CompactInputField
                          name={`${prefix}-notes`}
                          type="text"
                          placeholder="Nota para este ingrediente..."
                          disabled={saving}
                          value={ingredient.notes}
                          error={errors[`${prefix}-notes`]}
                          onChange={(event) =>
                            onIngredientChange(
                              ingredient.key,
                              'notes',
                              event.target.value
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {form.ingredients.length > 1 && (
            <div className="border-t border-stroke p-2 dark:border-strokedark">
              <button
                type="button"
                disabled={saving}
                onClick={onAddIngredient}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-stroke py-2 text-sm font-medium text-primary transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-60 dark:border-strokedark"
              >
                <Plus className="h-4 w-4" />
                Agregar ingrediente
              </button>
            </div>
          )}
        </section>

        {/* =====================================================
            🔹 Estado (solo al editar)
        ===================================================== */}

        {form.id && (
          <div className="rounded-xl border border-stroke bg-gray-2/30 px-4 py-3 dark:border-strokedark dark:bg-meta-4/30">
            <ToggleSwitch
              label="Receta activa"
              name="recipeActive"
              checked={form.active}
              onChange={() => onChange('active', !form.active)}
            />
          </div>
        )}

        {/* =====================================================
            🔹 Producción
        ===================================================== */}

        {isOpen &&
          branchId &&
          form.id &&
          form.inventoryMode === 'PRODUCTION_BATCH' && (
            <RestaurantRecipeProductionPanel
              branchId={branchId}
              recipeId={form.id}
              initialQuantity={form.yieldQuantity || 1}
            />
          )}

        {/* =====================================================
            🔹 Acciones
        ===================================================== */}

        <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-5 dark:border-strokedark sm:flex-row sm:justify-end">
          <ActionButton
            label="Cancelar"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={saving}
            onClick={onClose}
            className="sm:min-w-[120px]"
          />

          <ActionButton
            type="submit"
            label="Guardar receta"
            loadingLabel="Guardando..."
            variant="primary"
            size="md"
            fullWidth={false}
            loading={saving}
            disabled={loadingOptions || hasErrors}
            className="sm:min-w-[160px]"
          />
        </div>
      </form>

      {branchId && pickingIngredientKey !== null && (() => {
        const ingredient = form.ingredients.find((item) => item.key === pickingIngredientKey);
        if (!ingredient) return null;
        return (
          <PagedRawMaterialPicker
            isOpen
            branchId={branchId}
            units={units}
            selectedIds={form.ingredients
              .filter((item) => item.rawMaterialId !== '')
              .map((item) => Number(item.rawMaterialId))}
            currentId={ingredient.rawMaterialId === '' ? null : Number(ingredient.rawMaterialId)}
            onClose={() => setPickingIngredientKey(null)}
            onSelect={(material) => handleMaterialChange(ingredient, material)}
          />
        );
      })()}

      {branchId && (
        <MenuItemQuickCreateModal
          isOpen={menuItemQuickOpen}
          branchId={Number(branchId)}
          saving={menuItemQuickSaving}
          onClose={() => setMenuItemQuickOpen(false)}
          onSubmit={handleCreateMenuItem}
        />
      )}
    </FormModal>
  );
};

export default RestaurantRecipeFormModal;
